import React, { useState, useRef, useEffect } from "react";
import { Button, Card, Typography, Space, message } from "antd";
import { AudioOutlined, StopOutlined } from "@ant-design/icons";
import ReconnectingWebSocket from "reconnecting-websocket";
import { GeneralService } from "./services/GeneralService";

const { Text } = Typography;

interface TranscriptionResponse {
  text: string;
  status: "success" | "error";
  error?: string;
  message?: string;
  transcript?: string;
  is_final?: boolean;
  processing_time?: number;
  cleaning_time?: number;
  transcription_time?: number;
}

const VoiceDemo: React.FC = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [transcription, setTranscription] = useState<string>("");
  const [isConnected, setIsConnected] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const wsRef = useRef<ReconnectingWebSocket | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);

  const initializeWebSocket = () => {
    if (wsRef.current) {
      return; // Already initialized
    }

    // Initialize WebSocket connection with reconnection
    wsRef.current = new ReconnectingWebSocket(
      `${GeneralService.BREEZE_ENDPOINT}/breeze/ws`,
      [],
      {
        maxRetries: 10,
        reconnectionDelayGrowFactor: 1.3,
        maxReconnectionDelay: 10000,
        minReconnectionDelay: 1000,
      }
    );

    wsRef.current.onopen = () => {
      setIsConnected(true);
      message.success("Connected to server");
    };

    wsRef.current.onclose = (event) => {
      setIsConnected(false);
      message.error("Disconnected from server");
    };

    wsRef.current.onerror = (error) => {
      message.error("WebSocket error occurred");
    };

    wsRef.current.onmessage = (event) => {
      try {
        const response: TranscriptionResponse = JSON.parse(event.data);
        if (response.status === "success") {
          if (response.transcript) {
            setTranscription(response.transcript);
          }
        } else if (response.status === "error") {
          message.error(response.error || "Transcription error occurred");
        }
      } catch (error) {
        message.error("Error processing server response");
      }
    };
  };

  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
      // Clean up MediaStream tracks on component unmount
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => {
          track.stop();
        });
      }
    };
  }, []);

  const startRecording = async () => {
    try {
      // Initialize WebSocket connection when user starts recording
      initializeWebSocket();

      // Wait for WebSocket to connect before proceeding
      if (wsRef.current && wsRef.current.readyState !== WebSocket.OPEN) {
        message.info("Connecting to server...");
        // Wait for connection with timeout
        await new Promise<void>((resolve, reject) => {
          const timeout = setTimeout(() => {
            reject(new Error("Connection timeout"));
          }, 10000);

          const checkConnection = () => {
            if (wsRef.current?.readyState === WebSocket.OPEN) {
              clearTimeout(timeout);
              resolve();
            } else {
              setTimeout(checkConnection, 100);
            }
          };
          checkConnection();
        });
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: 16000,
        },
      });
      mediaStreamRef.current = stream;

      // Create audio context
      const audioContext = new AudioContext({
        sampleRate: 16000,
        latencyHint: "interactive",
      });
      audioContextRef.current = audioContext;

      // Create audio source from stream
      const source = audioContext.createMediaStreamSource(stream);

      // Create script processor for raw PCM data
      const processor = audioContext.createScriptProcessor(4096, 1, 1);
      processorRef.current = processor;

      processor.onaudioprocess = (e) => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          // Get the raw PCM data
          const inputData = e.inputBuffer.getChannelData(0);

          // Convert Float32Array to Int16Array
          const pcmData = new Int16Array(inputData.length);
          for (let i = 0; i < inputData.length; i++) {
            pcmData[i] = Math.max(
              -32768,
              Math.min(32767, Math.round(inputData[i] * 32767))
            );
          }

          // Convert to base64
          const base64Audio = btoa(
            String.fromCharCode.apply(
              null,
              Array.from(new Uint8Array(pcmData.buffer))
            )
          );

          const message = {
            type: "audio",
            audio: base64Audio,
            format: "raw",
            sampleRate: 16000,
            channels: 1,
          };
          wsRef.current.send(JSON.stringify(message));
        }
      };

      // Connect the nodes
      source.connect(processor);
      processor.connect(audioContext.destination);

      // Send start message
      wsRef.current?.send(JSON.stringify({ type: "start" }));

      setIsRecording(true);
      setTranscription(""); // Clear previous transcription
      message.success("Started recording");
    } catch (err) {
      if (err instanceof Error && err.message === "Connection timeout") {
        message.error(
          "Failed to connect to server. Please check if the server is running."
        );
      } else {
        message.error("Failed to access microphone. Please check permissions.");
      }
    }
  };

  const stopRecording = () => {
    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    // Stop all MediaStream tracks to release microphone access
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => {
        track.stop();
      });
      mediaStreamRef.current = null;
    }

    setIsRecording(false);

    // Send stop message to force final transcription
    wsRef.current?.send(JSON.stringify({ type: "stop" }));
    message.success("Stopped recording");
  };

  return (
    <Card
      title="Voice Transcription Demo"
      style={{ maxWidth: 600, margin: "20px auto" }}>
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <Button
          type={isRecording ? "primary" : "default"}
          danger={isRecording}
          icon={isRecording ? <StopOutlined /> : <AudioOutlined />}
          onClick={isRecording ? stopRecording : startRecording}
          size="large"
          block>
          {isRecording ? "Stop Recording" : "Start Recording"}
        </Button>

        {wsRef.current && !isConnected && (
          <Text type="warning">Connecting to server...</Text>
        )}

        {transcription && (
          <Card size="small" title="Transcription">
            <Text>{transcription}</Text>
          </Card>
        )}
      </Space>
    </Card>
  );
};

export default VoiceDemo;
